/// <reference types="google.maps" /
import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnInit, SimpleChanges } from '@angular/core';
import { EnvironmentConfig, Listing } from '@clooper/core';
import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import * as uuid from 'uuid';


@Component({
  selector: 'app-corporate-map',
  templateUrl: './corporate-map.component.html',
  styleUrls: ['./corporate-map.component.scss']
})
export class CorporateMapComponent implements OnInit {
  id = 'm' + uuid.v4();
  private markers: any[] = [];
  private loader: any;
  private map: google.maps.Map;
  private geocoder: google.maps.Geocoder;
  private infoWindow: google.maps.InfoWindow;
  private clusterInfoWindow: google.maps.InfoWindow;
  private markersCluster: MarkerClusterer;
  clickedMarker: google.maps.Marker;
  selectedListing: Listing
  @Input() listings: Listing[] = [];
  @Input() employees: any[] = [];
  @Input() bookings: any[] = [];

  popupFlag: boolean;
  selectedEmployee: any;
  selectedBooking: any


  constructor(
    private config: EnvironmentConfig,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.loader = new Loader({
      apiKey: this.config.google_api_key as string,
      libraries: ['places'],
      version: 'weekly'
    });
  }

  ngOnInit(): void {
    this.loadMap()
  }

  loadMap() {
    this.loader.load().then(() => {
      this.clickedMarker = new google.maps.Marker()


      // Dashboard world-map style — "hide everything, opt in to what we want"
      // This approach guarantees no stray dashed lines from maritime, terrain, or
      // disputed-border layers that we couldn't identify by feature type.
      const dashboardWorldStyle: google.maps.MapTypeStyle[] = [
        // BLANK SLATE: hide every feature, every element
        { stylers: [{ visibility: "off" }] },

        // OPT IN: land geometry — warm off-white
        { featureType: "landscape", elementType: "geometry", stylers: [{ visibility: "on" }, { color: "#EAE8E2" }] },

        // OPT IN: water geometry — calm blue-grey
        { featureType: "water", elementType: "geometry", stylers: [{ visibility: "on" }, { color: "#D4E3E5" }] },

        // OPT IN: country borders — subtle muted grey stroke only
        { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ visibility: "on" }, { color: "#B9BDC1" }, { weight: 0.6 }] },

        // OPT IN: country labels — muted grey, no stroke
        { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ visibility: "on" }, { color: "#6B7280" }] },

        // OPT IN: city labels — slightly lighter grey, Google handles zoom-breakpoints automatically
        // (big capitals show at lower zoom, smaller cities show only when zoomed in further)
        { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ visibility: "on" }, { color: "#8F949A" }] },
        { featureType: "administrative.locality", elementType: "labels.text.stroke", stylers: [{ visibility: "on" }, { color: "#FFFFFF" }, { weight: 2 }] },
      ];


      this.map = new google.maps.Map(this.document.getElementById(this.id) as HTMLElement, {
        zoom: 2.4,
        minZoom: 2,
        maxZoom: 6, // cap zoom at country level — map is a snapshot view, not a drill-down tool
        center: { lat: 25, lng: 10 },
        mapTypeControl: false,
        streetViewControl: false,
        clickableIcons: false,
        styles: dashboardWorldStyle,
        gestureHandling: 'greedy',
        // Prevent the world from tiling/wrapping horizontally on wide screens
        restriction: {
          latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
          strictBounds: false,
        },

      });

      this.infoWindow = new google.maps.InfoWindow(
        {
          content: document.getElementById("content"),
        }
      );

      this.clusterInfoWindow = new google.maps.InfoWindow();

      // Click the map background → dismiss any open popup (covers both individual & cluster)
      this.map.addListener('click', () => {
        this.closeListingPopup();
        this.clusterInfoWindow?.close();
      });

      this.getListingsAndSetCenter()

      this.geocoder = new google.maps.Geocoder();


    });
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes.listings?.previousValue) {
      if (changes.listings?.currentValue[0] !== changes.listings?.previousValue[0]) {
        this.removePreviousMarkers()
        this.getListingsAndSetCenter()
      }
    }

  }

  removePreviousMarkers() {
    if (this.markers) {
      for (var j = 0; j < this.markers.length; j++) {
        this.markers[j].marker.setMap(null);
      }
      this.markers.length = 0;
      this.markersCluster.clearMarkers();

    }
  }

  getListingsAndSetCenter() {
    this.listings?.forEach((listing, i) => {
      this.createListingMarker(listing, i)
    })

    let clusterMarkers = this.markers.map((item) => {
      return item.marker
    })

    // Cluster marker — Clooper darkgreen filled circle with translucent halo
    const CLUSTER_ICON = {
      url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent('<svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="24" r="22" fill="#27575D" opacity="0.18"/><circle cx="24" cy="24" r="17" fill="#27575D"/></svg>'),
      labelOrigin: new google.maps.Point(24, 24)
    }

    const renderer = {
      render({ count, position }: any) {
        return new google.maps.Marker({
          label: { text: String(count), color: "#FFFFFF", fontSize: "13px", fontWeight: "600" },
          position,
          icon: CLUSTER_ICON
        })
      }
    }


    this.markersCluster = new MarkerClusterer({
      markers: clusterMarkers,
      map: this.map,
      renderer,
      // Clusters stay aggregated — map caps at country-level zoom (6), so clusters never dissolve into individual pins
      algorithm: new SuperClusterAlgorithm({ maxZoom: 5 }),
      // Override default zoom-in behaviour — cluster click flies over to centroid and opens a snapshot popup
      onClusterClick: (_event, cluster) => {
        this.handleClusterClick(cluster);
      },
    });


    if (this.listings.length) {
      this.selectedListing = this.listings[0]
      this.selectedEmployee = this.employees[0]
      this.selectedBooking = this.bookings[0]
    }

    let center = this.getLatLng(this.selectedListing)

    this.map.setCenter(center);


  }

  createListingMarker(listing: Listing, i: number) {

    // Traveller marker — Clooper darkgreen filled circle with white ring
    const TRAVELLER_MARKER = {
      url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent('<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" fill="#FFFFFF" stroke="#27575D" stroke-width="2"/><circle cx="12" cy="12" r="5" fill="#27575D"/></svg>'),
      anchor: new google.maps.Point(12, 12),
      labelOrigin: new google.maps.Point(12, 12)
    }

    const label = {
      text: ' ',
      fontWeight: 'bold',
      color: 'black'
    }

    const employee = this.employees?.[i];
    const booking = this.bookings?.[i];

    let pos: google.maps.LatLngLiteral
    if (listing?.location?.coordinates) {
      pos = { lat: this.offsetMarker(listing?.location?.coordinates[0]) || 51.5072, lng: this.offsetMarker(listing?.location?.coordinates[1]) || 0.1276 }
    } else {
      pos = { lat: this.offsetMarker((listing as any)?.location[0]) || 51.5072, lng: this.offsetMarker((listing as any)?.location[1]) || 0.1276 }
    }
    const marker: google.maps.Marker = new google.maps.Marker({
      map: this.map, position: pos, icon: TRAVELLER_MARKER, label,
    });
    if (i == 0) {
      marker.setZIndex(this.markers.length + 1)
    }

    // Store employee + booking alongside the marker so the cluster popup can aggregate by person
    this.markers.push({ marker, listing, employee, booking })


    //Event for marker click — opens the existing per-trip popup
    google.maps.event.addListener(marker, 'click', ((marker, infoWindow) => {
      return () => {

        for (var j = 0; j < this.markers.length; j++) {
          this.markers[j].marker.setZIndex(j);
        }

        this.popupFlag = true
        this.selectedListing = listing
        this.selectedEmployee = employee
        this.selectedBooking = booking
        let options = {
          pixelOffset: new google.maps.Size(0, 25)
        }
        infoWindow.open({
          anchor: marker,
          map: this.map,
          shouldFocus: false,

        });
        infoWindow.setOptions(options)
      }

    })(marker, this.infoWindow));

  }


  offsetMarker(value: number) {
    // return value + (Math.random() - 1) / 1500;
    return value
  }
  getLatLng(listing: Listing) {
    let latlng
    if (listing?.location?.coordinates) {
      latlng = { lat: listing?.location?.coordinates[0] || 51.5072, lng: listing?.location?.coordinates[1] || 0.1276 }
    } else {
      latlng = { lat: (listing as any)?.location[0] || 51.5072, lng: (listing as any)?.location[1] || 0.1276 }
    }
    return latlng
  }


  closeListingPopup() {
    this.popupFlag = false;
    this.infoWindow?.close();
  }

  handleClusterClick(cluster: any) {
    // Close any open individual-marker popup
    this.closeListingPopup();

    // Smooth flyover — pan to cluster centroid, no zoom change (map stays at country view)
    if (cluster?.position) {
      this.map.panTo(cluster.position);
    }

    // Resolve marker records inside this cluster — each is one trip
    const clusterRecords: any[] = (cluster?.markers || [])
      .map((m: google.maps.Marker) => this.markers.find(x => x.marker === m))
      .filter(Boolean);

    // Group by employee. Each employee gets a trip count (within this cluster) and a search term for the filtered list.
    const byEmployee = new Map<string, { name: string; count: number; searchTerm: string }>();
    clusterRecords.forEach((rec: any) => {
      const emp = rec.employee;
      const first = emp?.user?.first_name || '';
      const last = emp?.user?.last_name || '';
      const fullName = `${first} ${last}`.trim() || emp?.user?.email || 'Unknown';
      const key = emp?.id || emp?._id || emp?.user?.id || fullName;
      const searchTerm = fullName;
      if (!byEmployee.has(key)) {
        byEmployee.set(key, { name: fullName, count: 0, searchTerm });
      }
      byEmployee.get(key)!.count += 1;
    });

    const sortedEmployees = Array.from(byEmployee.values()).sort((a, b) => b.count - a.count);
    const TOP_N = 5;
    const topEmployees = sortedEmployees.slice(0, TOP_N);
    const remainder = sortedEmployees.length - topEmployees.length;

    const totalTrips = clusterRecords.length;
    const tripLabel = totalTrips === 1 ? 'trip' : 'trips';
    const uniquePeople = sortedEmployees.length;
    const personLabel = uniquePeople === 1 ? 'team member' : 'team members';

    const employeeRows = topEmployees.map(e => {
      const href = `/corporate-account/business-trips/accomodation?search=${encodeURIComponent(e.searchTerm)}`;
      const tripWord = e.count === 1 ? 'trip' : 'trips';
      return `<a href="${href}" style="display:flex;justify-content:space-between;align-items:center;font-size:13px;color:#374151;padding:7px 0;text-decoration:none;border-bottom:1px solid #F3F4F6;"><span style="font-weight:500;">${e.name}</span><span style="color:#6B7280;font-size:12px;">${e.count} ${tripWord} →</span></a>`;
    }).join('');

    const remainderRow = remainder > 0
      ? `<div style="font-size:12px;color:#6B7280;padding:8px 0 0;font-style:italic;">+ ${remainder} more ${remainder === 1 ? 'traveller' : 'travellers'}</div>`
      : '';

    const content = `
      <div style="font-family:'Poppins',-apple-system,sans-serif;background:#ffffff;border-radius:12px;box-shadow:0 10px 30px rgba(17,24,39,0.18);padding:16px 18px;min-width:260px;max-width:300px;position:relative;">
        <button type="button" class="ca-cluster-close" aria-label="Close" style="position:absolute;top:6px;right:8px;background:transparent;border:0;padding:4px 8px;font-size:22px;line-height:1;color:#9CA3AF;cursor:pointer;">&times;</button>
        <div style="font-size:22px;font-weight:600;color:#27575D;line-height:1.1;padding-right:16px;">${totalTrips} ${tripLabel}</div>
        <div style="font-size:12px;color:#6B7280;margin-top:4px;margin-bottom:10px;">${uniquePeople} ${personLabel}</div>
        ${employeeRows}
        ${remainderRow}
        <a href="/corporate-account/business-trips/accomodation" style="display:block;margin-top:12px;padding-top:12px;border-top:1px solid #E5E7EB;font-size:13px;font-weight:500;color:#27575D;text-decoration:none;">View all trips →</a>
      </div>`;

    this.clusterInfoWindow.setContent(content);
    this.clusterInfoWindow.setPosition(cluster.position);
    this.clusterInfoWindow.open({ map: this.map });

    // Wire the close button once the popup is attached to DOM (each open is a fresh listener)
    google.maps.event.addListenerOnce(this.clusterInfoWindow, 'domready', () => {
      const btn = document.querySelector('.ca-cluster-close') as HTMLElement | null;
      if (btn) {
        btn.onclick = (e) => {
          e.stopPropagation();
          this.clusterInfoWindow.close();
        };
      }
    });
  }

}
